/**
 * Global error handler for chunk loading failures
 * Automatically retries or reloads the page when Vite/Webpack chunks fail to load
 */

let reloadAttempts = 0;
const MAX_RELOAD_ATTEMPTS = 2;
const RELOAD_KEY = 'chunk_reload_count';
const RELOAD_TIMESTAMP_KEY = 'chunk_reload_timestamp';
const RELOAD_TIMEOUT = 10000; // 10 seconds

/**
 * Check if error is a chunk loading error
 */
function isChunkLoadError(error: any): boolean {
  const errorMessage = error?.message || String(error);

  return (
    error?.name === 'ChunkLoadError' ||
    errorMessage.includes('Failed to fetch dynamically imported module') ||
    errorMessage.includes('Loading chunk') ||
    errorMessage.includes('Importing a module script failed') ||
    errorMessage.includes('dynamically imported module')
  );
}

/**
 * Get reload count from session storage
 */
function getReloadCount(): number {
  try {
    const count = sessionStorage.getItem(RELOAD_KEY);
    const timestamp = sessionStorage.getItem(RELOAD_TIMESTAMP_KEY);

    if (timestamp) {
      const elapsed = Date.now() - parseInt(timestamp, 10);
      // Reset count if last reload was more than 10 seconds ago
      if (elapsed > RELOAD_TIMEOUT) {
        sessionStorage.removeItem(RELOAD_KEY);
        sessionStorage.removeItem(RELOAD_TIMESTAMP_KEY);
        return 0;
      }
    }

    return count ? parseInt(count, 10) : 0;
  } catch {
    return 0;
  }
}

/**
 * Increment reload count
 */
function incrementReloadCount(): void {
  try {
    const count = getReloadCount() + 1;
    sessionStorage.setItem(RELOAD_KEY, count.toString());
    sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, Date.now().toString());
  } catch {
    // Ignore storage errors
  }
}

/**
 * Reset reload count
 */
function resetReloadCount(): void {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
    sessionStorage.removeItem(RELOAD_TIMESTAMP_KEY);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Handle chunk load error with automatic reload
 */
function handleChunkLoadError(error: any): void {
  console.error('Chunk load error detected:', error);

  reloadAttempts = getReloadCount();

  if (reloadAttempts < MAX_RELOAD_ATTEMPTS) {
    console.log(`Attempting to reload page (attempt ${reloadAttempts + 1}/${MAX_RELOAD_ATTEMPTS})...`);
    incrementReloadCount();

    // Add a small delay before reloading
    setTimeout(() => {
      window.location.reload();
    }, 500);
  } else {
    console.error('Max reload attempts reached. Please clear cache and refresh manually.');
    resetReloadCount();

    // Show user-friendly error message
    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: white;
      padding: 32px;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
      z-index: 999999;
      max-width: 400px;
      text-align: center;
      font-family: system-ui, -apple-system, sans-serif;
    `;

    errorDiv.innerHTML = `
      <h2 style="color: #dc2626; margin-bottom: 16px; font-size: 24px; font-weight: 600;">
        Update Required
      </h2>
      <p style="color: #4b5563; margin-bottom: 24px; line-height: 1.5;">
        A new version of the application is available. Please clear your browser cache and refresh the page.
      </p>
      <button
        onclick="window.location.reload(true)"
        style="
          background: #2563eb;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        "
        onmouseover="this.style.background='#1d4ed8'"
        onmouseout="this.style.background='#2563eb'"
      >
        Hard Refresh Page
      </button>
      <p style="color: #6b7280; margin-top: 16px; font-size: 14px;">
        Or press Ctrl+Shift+R (Cmd+Shift+R on Mac)
      </p>
    `;

    document.body.appendChild(errorDiv);
  }
}

/**
 * Initialize global chunk load error handler
 */
export function initChunkLoadErrorHandler(): void {
  // Reset count on successful page load
  window.addEventListener('load', () => {
    resetReloadCount();
  });

  // Handle unhandled promise rejections (most common for chunk load errors)
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault(); // Prevent error from appearing in console repeatedly
      handleChunkLoadError(event.reason);
    }
  });

  // Handle regular errors
  window.addEventListener('error', (event) => {
    if (isChunkLoadError(event.error || event.message)) {
      event.preventDefault();
      handleChunkLoadError(event.error || event.message);
    }
  });

  console.log('Chunk load error handler initialized');
}
